/**
 * sl-mkx.2.3: MarketplaceUpdateModal
 *
 * Shows a changelog diff between the installed version and the latest
 * published version, then triggers an in-place update via the
 * POST /installs/:installId/update endpoint.
 *
 * sl-mkx.6-rework-3 (F-UX-4): when the version range introduces new required
 * credential slots, the modal now renders a rebind UI per slot instead of a
 * single ack-only checkbox. Same-type workspace credentials are auto-matched
 * via `autoMatchCredentials` (same-service filter + stable ordering) so the
 * user only has to override when there are multiple candidates. The chosen
 * mapping is forwarded as `bindings.credentialMappings` so the backend
 * concretizer can wire the new slots during the in-place update.
 */
import { useLocalePicker } from '@affine/core/hooks/use-locale-picker';
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useWorkspaceCredentials } from '../setting/workspace-setting/credentials/use-workspace-credentials';
import {
  autoMatchCredentials,
  type CredentialMatchSuggestion,
} from './auto-match-credentials';
import { pickMarketplaceChangelog } from './changelog-i18n';
import { supportsMarketplaceReinstall } from './template-kind';
import * as styles from './update-modal.css';
import { useMarketplaceActions } from './use-marketplace-actions';
import type {
  MarketplaceTemplate,
  MarketplaceVersionBreakingHint,
} from './use-marketplace-api';
import { useMarketplaceVersionRange } from './use-marketplace-api';
import {
  type TemplateDependencyInfo,
  useTemplatePrerequisites,
} from './use-template-prerequisites';

export interface MarketplaceUpdateModalProps {
  installId: string;
  template: MarketplaceTemplate;
  templateId: string;
  templateTitle: string;
  workspaceId: string;
  currentVersion: number;
  currentSemanticVersion?: string | null;
  latestVersion: number;
  latestSemanticVersion?: string | null;
  installedDocIds: string[];
  onClose: () => void;
  onUpdated?: (result: { fromVersion: number; toVersion: number }) => void;
}

export function MarketplaceUpdateModal({
  installId,
  template,
  templateId,
  templateTitle,
  workspaceId,
  currentVersion,
  currentSemanticVersion: currentSemanticVersionInput,
  latestVersion,
  latestSemanticVersion,
  installedDocIds,
  onClose,
  onUpdated,
}: MarketplaceUpdateModalProps) {
  const t = useI18n();
  const picker = useLocalePicker();
  const { reinstallTemplate } = useMarketplaceActions(workspaceId);
  const [targetVersion, setTargetVersion] = useState(latestVersion);
  useEffect(() => {
    setTargetVersion(latestVersion);
  }, [latestVersion]);
  const targetVersionOptions = useMemo(() => {
    const lowerBound = Math.max(currentVersion + 1, latestVersion - 4);
    const options: number[] = [];
    for (let version = latestVersion; version >= lowerBound; version -= 1) {
      options.push(version);
    }
    return options;
  }, [currentVersion, latestVersion]);
  const currentSemanticVersion = requireTemplateSemanticVersion(
    template,
    currentVersion,
    currentSemanticVersionInput
  );
  const targetSemanticVersion = requireTemplateSemanticVersion(
    template,
    targetVersion,
    targetVersion === latestVersion ? latestSemanticVersion : undefined
  );
  const {
    versions,
    isLoading,
    error: loadError,
    hasNewRequiredCredentials,
    newRequiredCredentials,
    hasNewRequiredConfigValues,
    newRequiredConfigValues,
  } = useMarketplaceVersionRange({
    workspaceId,
    templateId,
    fromVersion: currentVersion,
    toVersion: targetVersion,
  });
  const {
    prerequisites,
    isLoading: dependenciesLoading,
    error: dependenciesError,
    errorCode: dependenciesErrorCode,
  } = useTemplatePrerequisites(workspaceId, templateId, targetVersion);
  const { credentials: workspaceCredentials, isLoading: credsLoading } =
    useWorkspaceCredentials();

  const suggestions = useMemo<CredentialMatchSuggestion[]>(
    () =>
      autoMatchCredentials(
        newRequiredCredentials,
        workspaceCredentials.map(c => ({
          id: c.id,
          name: c.name,
          service: c.service,
        }))
      ),
    [newRequiredCredentials, workspaceCredentials]
  );

  // Selection state, keyed by slot type. Seeded from the auto-match
  // preselection and only replaced on explicit user change.
  const [selections, setSelections] = useState<Record<string, string>>({});
  useEffect(() => {
    setSelections(prev => {
      const next: Record<string, string> = {};
      for (const s of suggestions) {
        next[s.slotType] = prev[s.slotType] ?? s.selectedCredentialId ?? '';
      }
      return next;
    });
  }, [suggestions]);
  const [configSelections, setConfigSelections] = useState<
    Record<string, string>
  >({});
  useEffect(() => {
    setConfigSelections(prev => {
      const next: Record<string, string> = {};
      for (const config of newRequiredConfigValues) {
        next[config.key] = prev[config.key] ?? config.defaultValue ?? '';
      }
      return next;
    });
  }, [newRequiredConfigValues]);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const reinstallUpdate = supportsMarketplaceReinstall({
    templateType: template.templateType,
    installedDocIds,
  });
  const missingDependencies = useMemo(
    () =>
      (prerequisites?.dependencies?.dependencies ?? []).filter(
        dep => dep.status === 'missing'
      ),
    [prerequisites?.dependencies?.dependencies]
  );
  const confirmedDependencies = useMemo(
    () =>
      missingDependencies.map(dep => ({
        templateId: dep.templateId,
        versionNumber: dep.versionNumber,
        canonicalKey: dep.canonicalKey,
        majorVersion: dep.majorVersion,
        semanticVersion: dep.semanticVersion,
        contractHash: dep.contractHash,
      })),
    [missingDependencies]
  );
  const dependenciesErrorMessage = useMemo(() => {
    if (!dependenciesError) return null;
    return (
      formatUpdateErrorCode(t, dependenciesErrorCode) ??
      t['com.streamlain.marketplace.update.dependencies.error']()
    );
  }, [dependenciesError, dependenciesErrorCode, t]);

  const allRequiredMapped = useMemo(
    () =>
      suggestions.every(s =>
        s.required ? (selections[s.slotType] ?? '') !== '' : true
      ),
    [suggestions, selections]
  );
  const allRequiredConfigValuesMapped = useMemo(
    () =>
      newRequiredConfigValues.every(config =>
        config.required
          ? (configSelections[config.key] ?? '').trim() !== ''
          : true
      ),
    [configSelections, newRequiredConfigValues]
  );

  const canSubmit = useMemo(() => {
    if (isLoading || dependenciesLoading || isSubmitting || credsLoading) {
      return false;
    }
    if (loadError || dependenciesError) return false;
    if (targetVersion <= currentVersion) return false;
    if (hasNewRequiredCredentials && !allRequiredMapped) return false;
    if (hasNewRequiredConfigValues && !allRequiredConfigValuesMapped) {
      return false;
    }
    return true;
  }, [
    isLoading,
    dependenciesLoading,
    isSubmitting,
    credsLoading,
    loadError,
    dependenciesError,
    targetVersion,
    currentVersion,
    hasNewRequiredCredentials,
    allRequiredMapped,
    hasNewRequiredConfigValues,
    allRequiredConfigValuesMapped,
  ]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const credentialMappings: Record<string, string> = {};
      for (const s of suggestions) {
        const chosen = selections[s.slotType];
        if (chosen) credentialMappings[s.slotType] = chosen;
      }
      const configValues: Record<string, string> = {};
      for (const config of newRequiredConfigValues) {
        const chosen = (configSelections[config.key] ?? '').trim();
        if (chosen) configValues[config.key] = chosen;
      }
      const hasMappings = Object.keys(credentialMappings).length > 0;
      const hasConfigValues = Object.keys(configValues).length > 0;
      const bindings =
        hasMappings || hasConfigValues || confirmedDependencies.length > 0
          ? {
              credentialMappings,
              configValues,
              ...(confirmedDependencies.length > 0
                ? { confirmedDependencies }
                : {}),
            }
          : undefined;
      if (reinstallUpdate) {
        const res = await reinstallTemplate({
          installId,
          templateId,
          targetVersionNumber: targetVersion,
          ...(bindings ? { bindings } : {}),
          templateSummary: template,
        });
        if (!res) {
          setSubmitError(t['com.streamlain.marketplace.update.failed']());
          return;
        }
        onUpdated?.({
          fromVersion: currentVersion,
          toVersion: targetVersion,
        });
      } else {
        setSubmitError(t['com.streamlain.marketplace.update.failed']());
        return;
      }
      onClose();
    } catch (e: unknown) {
      setSubmitError(formatUpdateError(t, e));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    reinstallTemplate,
    installId,
    templateId,
    template,
    targetVersion,
    currentVersion,
    reinstallUpdate,
    onUpdated,
    onClose,
    suggestions,
    selections,
    newRequiredConfigValues,
    configSelections,
    confirmedDependencies,
    t,
  ]);

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sl-mkx-update-title"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.dialog}>
        <div className={styles.header}>
          <h2 id="sl-mkx-update-title" className={styles.title}>
            {t['com.streamlain.marketplace.update.title']()}
            <span className={styles.versionBump}>
              {currentSemanticVersion} {'→'} {targetSemanticVersion}
            </span>
          </h2>
          <div className={styles.subtitle}>{templateTitle}</div>
        </div>

        <div className={styles.body}>
          <section className={styles.section}>
            <label
              className={styles.sectionLabel}
              htmlFor="sl-mkx-target-version"
            >
              {t['com.streamlain.marketplace.update.target-version']()}
            </label>
            <select
              id="sl-mkx-target-version"
              className={styles.credentialSelect}
              value={targetVersion}
              onChange={event => setTargetVersion(Number(event.target.value))}
              disabled={isSubmitting}
            >
              {targetVersionOptions.map(version => (
                <option key={version} value={version}>
                  {requireTemplateSemanticVersion(template, version)}
                </option>
              ))}
            </select>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionLabel}>
              {t['com.streamlain.marketplace.update.whats-new']()}
            </div>
            {isLoading ? (
              <div className={styles.loadingText}>
                {t['com.streamlain.marketplace.update.loading-changelog']()}
              </div>
            ) : loadError ? (
              <div className={styles.errorText}>{loadError}</div>
            ) : versions.length === 0 ? (
              <div className={styles.emptyChangelog}>
                {t['com.streamlain.marketplace.update.empty-changelog']()}
              </div>
            ) : (
              <div className={styles.changelogList}>
                {versions.map(v => (
                  <div key={v.versionNumber} className={styles.changelogEntry}>
                    <div className={styles.changelogHeader}>
                      <span className={styles.changelogVersion}>
                        {v.semanticVersion}
                      </span>
                      <span className={styles.changelogDate}>
                        {formatDate(v.createdAt)}
                      </span>
                    </div>
                    <div className={styles.changelogText}>
                      {pickMarketplaceChangelog(v, picker)}
                    </div>
                    {v.breakingHints?.length ? (
                      <div className={styles.breakingHintList}>
                        {v.breakingHints.map((hint, index) => (
                          <div
                            key={`${hint.code}-${index}`}
                            className={
                              hint.severity === 'blocking'
                                ? styles.breakingHintBlocking
                                : styles.breakingHintWarning
                            }
                          >
                            {formatBreakingHint(t, hint)}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </section>

          {hasNewRequiredCredentials ? (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                {t['com.streamlain.marketplace.update.credentials.title']()}
              </div>
              <div className={styles.credentialWarning}>
                {t['com.streamlain.marketplace.update.credentials.warning']()}
              </div>
              <div className={styles.credentialSlotList}>
                {suggestions.map(s => (
                  <div key={s.slotType} className={styles.credentialSlotRow}>
                    <label className={styles.credentialSlotLabel}>
                      {s.slotLabel}
                      {s.required ? (
                        <span className={styles.credentialSlotRequired}>*</span>
                      ) : null}
                    </label>
                    {s.alternatives.length === 0 ? (
                      <div className={styles.credentialSlotHint}>
                        {t[
                          'com.streamlain.marketplace.update.credentials.none-of-type'
                        ]({ type: s.slotType })}
                      </div>
                    ) : (
                      <select
                        className={styles.credentialSelect}
                        value={selections[s.slotType] ?? ''}
                        onChange={e =>
                          setSelections(prev => ({
                            ...prev,
                            [s.slotType]: e.target.value,
                          }))
                        }
                      >
                        <option value="" disabled>
                          {t[
                            'com.streamlain.marketplace.update.credentials.select'
                          ]()}
                        </option>
                        {s.alternatives.map(alt => (
                          <option
                            key={alt.credentialId}
                            value={alt.credentialId}
                          >
                            {alt.credentialName}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {hasNewRequiredConfigValues ? (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                {t['com.streamlain.marketplace.update.config.title']()}
              </div>
              <div className={styles.credentialWarning}>
                {t['com.streamlain.marketplace.update.config.warning']()}
              </div>
              <div className={styles.credentialSlotList}>
                {newRequiredConfigValues.map(config => (
                  <div key={config.key} className={styles.credentialSlotRow}>
                    <label className={styles.credentialSlotLabel}>
                      {config.label ?? config.key}
                      <span className={styles.credentialSlotRequired}>*</span>
                    </label>
                    {config.description ? (
                      <div className={styles.credentialSlotHint}>
                        {config.description}
                      </div>
                    ) : null}
                    <input
                      className={styles.credentialSelect}
                      value={configSelections[config.key] ?? ''}
                      placeholder={t[
                        'com.streamlain.marketplace.update.config.placeholder'
                      ]()}
                      onChange={e =>
                        setConfigSelections(prev => ({
                          ...prev,
                          [config.key]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {dependenciesLoading ? (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                {t['com.streamlain.marketplace.update.dependencies.title']()}
              </div>
              <div className={styles.loadingText}>
                {t['com.streamlain.marketplace.update.dependencies.loading']()}
              </div>
            </section>
          ) : dependenciesError ? (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                {t['com.streamlain.marketplace.update.dependencies.title']()}
              </div>
              <div className={styles.errorText}>{dependenciesErrorMessage}</div>
            </section>
          ) : missingDependencies.length > 0 ? (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                {t['com.streamlain.marketplace.update.dependencies.title']()}
              </div>
              <div className={styles.credentialWarning}>
                {t['com.streamlain.marketplace.update.dependencies.warning']({
                  count: String(missingDependencies.length),
                })}
              </div>
              <div className={styles.credentialSlotList}>
                {missingDependencies.map(dep => (
                  <DependencyRow key={dependencyRowKey(dep)} dependency={dep} />
                ))}
              </div>
            </section>
          ) : null}

          {submitError ? (
            <div className={styles.errorText}>{submitError}</div>
          ) : null}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.buttonSecondary}
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t['com.streamlain.marketplace.update.cancel']()}
          </button>
          <button
            type="button"
            className={styles.buttonPrimary}
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isSubmitting
              ? t['com.streamlain.marketplace.update.submitting']()
              : t['com.streamlain.marketplace.update.submit']({
                  version: targetSemanticVersion,
                })}
          </button>
        </div>
      </div>
    </div>
  );
}

function dependencyRowKey(dependency: TemplateDependencyInfo): string {
  return [
    dependency.templateId,
    dependency.versionNumber,
    dependency.canonicalKey,
    dependency.contractHash,
  ].join(':');
}

function DependencyRow({ dependency }: { dependency: TemplateDependencyInfo }) {
  return (
    <div className={styles.credentialSlotRow}>
      <div className={styles.credentialSlotLabel}>{dependency.title}</div>
      <div className={styles.credentialSlotHint}>
        {dependency.templateType} · {dependency.semanticVersion}
      </div>
    </div>
  );
}

function requireTemplateSemanticVersion(
  template: MarketplaceTemplate,
  versionNumber: number,
  explicitSemanticVersion?: string | null
): string {
  if (
    typeof explicitSemanticVersion === 'string' &&
    explicitSemanticVersion.length > 0
  ) {
    return explicitSemanticVersion;
  }
  const semanticVersion = template.versions?.find(
    version => version.versionNumber === versionNumber
  )?.semanticVersion;
  if (typeof semanticVersion === 'string' && semanticVersion.length > 0) {
    return semanticVersion;
  }
  throw new Error(
    `Marketplace template ${template.id} is missing semanticVersion for version ${versionNumber}`
  );
}

function formatUpdateError(
  t: ReturnType<typeof useI18n>,
  error: unknown
): string {
  const code =
    error instanceof Error
      ? (error as Error & { code?: string }).code
      : undefined;
  const codeMessage = formatUpdateErrorCode(t, code);
  if (codeMessage) return codeMessage;
  if (
    error instanceof Error &&
    error.message === 'Marketplace template dependencies must be confirmed'
  ) {
    return t[
      'com.streamlain.marketplace.update.error.dependencies-unconfirmed'
    ]();
  }
  return t['com.streamlain.marketplace.update.failed']();
}

function formatUpdateErrorCode(
  t: ReturnType<typeof useI18n>,
  code: string | null | undefined
): string | null {
  if (code === 'MARKETPLACE_DEPENDENCIES_UNCONFIRMED') {
    return t[
      'com.streamlain.marketplace.update.error.dependencies-unconfirmed'
    ]();
  }
  if (
    code === 'MARKETPLACE_DEPENDENCY_INSTALLED_VERSION_CONFLICT' ||
    code === 'MARKETPLACE_DEPENDENCY_VERSION_CONFLICT'
  ) {
    return t[
      'com.streamlain.marketplace.update.error.dependency-version-conflict'
    ]();
  }
  return null;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function formatBreakingHint(
  t: ReturnType<typeof useI18n>,
  hint: MarketplaceVersionBreakingHint
): string {
  if (hint.code === 'new_required_credentials') {
    const credentialTypes = (hint as { credentialTypes?: unknown })
      .credentialTypes;
    const count = Array.isArray(credentialTypes) ? credentialTypes.length : 1;
    return t[
      'com.streamlain.marketplace.update.breaking-hint.new-required-credentials'
    ]({
      count: String(count),
    });
  }
  if (hint.code === 'new_required_config_values') {
    const configKeys = (hint as { configKeys?: unknown }).configKeys;
    const count = Array.isArray(configKeys) ? configKeys.length : 1;
    return t[
      'com.streamlain.marketplace.update.breaking-hint.new-required-config-values'
    ]({
      count: String(count),
    });
  }
  if (hint.code === 'semver_major') {
    const semanticVersion =
      typeof hint.semanticVersion === 'string' ? hint.semanticVersion : null;
    if (!semanticVersion) {
      throw new Error(
        'Marketplace semver_major hint is missing semanticVersion'
      );
    }
    return t['com.streamlain.marketplace.update.breaking-hint.semver-major']({
      version: semanticVersion,
    });
  }
  return t['com.streamlain.marketplace.update.breaking-hint.generic']({
    code: hint.code,
  });
}
